/**
 * AdminHustleMeetHostAppsPage.jsx — /admin/hustlemeet-host-apps
 *
 * Q9 — admin can see applicants' reliability score + ID-check status
 * at host-application review time. Uses the enriched
 * /admin/hustlemeet-circles/host-apps response.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Users, CheckCircle2, ShieldCheck, ShieldAlert, RefreshCw, X } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const authHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function AdminHustleMeetHostAppsPage() {
  const [apps, setApps]   = useState([]);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState("");
  const [filter, setFilter] = useState("pending");

  const cfg = { headers: authHeader(), withCredentials: true };

  const load = async () => {
    setBusy(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const { data } = await axios.get(
        `${API}/admin/hustlemeet-circles/host-apps${q}`, cfg);
      setApps(data?.applications || []);
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  useEffect(() => {
    document.title = "Admin · HustleMeet Host Applications";
    load();
  }, [filter]);

  const decide = async (id, kind) => {
    try {
      await axios.post(
        `${API}/admin/hustlemeet-circles/host-apps/${id}/${kind}`, {}, cfg);
      toast.success(`Application ${kind}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${kind} failed`);
    }
  };

  const pct = (v) => (v == null ? "—" : `${v}%`);

  const scoreBadge = (rel) => {
    const sr = rel?.show_rate;
    if (sr == null) return "bg-stone-500/10 text-stone-500";
    if (sr >= 90)   return "bg-emerald-500/15 text-emerald-500";
    if (sr >= 75)   return "bg-amber-500/15 text-amber-500";
    return "bg-red-500/15 text-red-500";
  };

  if (err) return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0a0908] flex items-center justify-center p-6">
      <div className="max-w-md text-center" data-testid="hostapps-err">
        <p className="text-lg font-black">Access denied</p>
        <p className="mt-2 text-sm text-stone-500">{err}</p>
      </div>
    </div>
  );

  return (
    <div
      data-testid="admin-hustlemeet-host-apps-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100"
    >
      <PlatformHeader
        brand={{ name: "Admin · Host Apps", to: "/admin/hustlemeet-host-apps" }}
        showBookCta={false} showCart={false}
      />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <header className="mb-6 flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
            <Users size={20}/>
          </div>
          <div className="flex-1">
            <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">Host applications</p>
            <h1 className="mt-1 text-2xl md:text-3xl font-black">Vet before approving</h1>
            <p className="mt-1 text-xs text-stone-500 max-w-lg">
              Every applicant's reliability score, no-show count, and ID-check status is shown here
              so you can approve confidently.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <select data-testid="status-filter" value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="rounded-full border border-stone-200 dark:border-stone-800 px-3 py-1 text-xs bg-transparent">
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="all">All</option>
            </select>
            <Button onClick={load} variant="outline" className="rounded-full font-black">
              <RefreshCw size={14} className="mr-1"/> Refresh
            </Button>
          </div>
        </header>

        {busy ? (
          <p className="text-sm text-stone-500" data-testid="hostapps-loading">Loading…</p>
        ) : apps.length === 0 ? (
          <p className="text-sm text-stone-500" data-testid="hostapps-empty">No applications in this state.</p>
        ) : (
          <ul className="space-y-3">
            {apps.map((a) => (
              <li
                key={a.id}
                data-testid={`hostapp-${a.id}`}
                className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-4"
              >
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-black">{a.member_email || a.member_id}</p>
                    <p className="text-[11px] text-stone-500">
                      Proposed: {a.proposed_venue_name || "—"} · Applied {a.created_at?.slice(0,10) || ""}
                    </p>
                  </div>
                  <span
                    data-testid={`reliability-${a.id}`}
                    className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full ${scoreBadge(a.reliability)}`}
                    title="Show-rate = attended / bookings"
                  >
                    Show-rate {pct(a.reliability?.show_rate)}
                    {a.reliability?.no_shows > 0 && ` · ${a.reliability.no_shows} no-shows`}
                  </span>
                  <span
                    data-testid={`idcheck-${a.id}`}
                    className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full ${
                      a.id_check?.verified
                        ? "bg-emerald-500/15 text-emerald-500"
                        : a.id_check?.status === "pending"
                          ? "bg-amber-500/15 text-amber-500"
                          : "bg-stone-500/10 text-stone-500"
                    }`}
                  >
                    {a.id_check?.verified
                      ? <ShieldCheck size={11}/>
                      : <ShieldAlert size={11}/>}
                    ID: {a.id_check?.status || "none"}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest font-black px-2 py-0.5 rounded-full bg-stone-500/10 text-stone-500">
                    {a.status}
                  </span>
                </div>
                <p className="mt-3 text-xs text-stone-600 dark:text-stone-300 line-clamp-3">
                  {a.experience}
                </p>
                {a.status === "pending" && (
                  <div className="mt-3 flex justify-end gap-2">
                    <Button
                      variant="outline" className="rounded-full font-black h-9 text-xs"
                      data-testid={`btn-reject-${a.id}`}
                      onClick={() => decide(a.id, "reject")}
                    >
                      <X size={12} className="mr-1"/> Reject
                    </Button>
                    <Button
                      className="rounded-full bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-black h-9 text-xs"
                      data-testid={`btn-approve-${a.id}`}
                      onClick={() => decide(a.id, "approve")}
                    >
                      <CheckCircle2 size={12} className="mr-1"/> Approve
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      <SuiteFooter />
    </div>
  );
}
